<template>
  <p-product-layout
    data-test-product-layout
    :product="product"
    :supplier-related-products="supplierRelatedProducts"
    :similar-products="similarProducts"
    :frequently-bought-together-products="frequentlyBoughtTogetherProducts"
    :supplier-related-product-count="supplierRelatedProductCount"
    :is-authenticated="isAuthenticated"
    :cooperative-name="cooperativeName"
    :technical-sales="profile.technicalSales"
    :show-technical-sales-contact="showTechnicalSalesContact"
    :banner="banner"
    @login="login()"
    @go-to-product-search="goToProductSearch"
  >
    <template #product-alert>
      <p-product-explosives-precursor-card
        v-if="product.isRestrictedExplosivesPrecursor && cooperativeName"
        class="product-alert__explosives-precursor-card"
        :cooperative-name="cooperativeName"
        :is-explosives-precursor-allowed="isExplosivesPrecursorAllowed"
        :lines="2"
        :technical-sales="profile.technicalSales"
      />
      <p-alert
        v-if="featuresForCurrentBrand.certificates.phyto && cooperativeName && !isCropProtectionAllowed && isPhyto"
        level="error"
        data-test="crop-protection-alert-error"
      >
        {{
          i18n._(
            'Aladin ne dispose pas de votre certiphyto, merci de contacter votre coopérative\
 afin de pouvoir commander les produits santé du végétal sur la plateforme',
          )
        }}
      </p-alert>
    </template>
    <template v-if="isPhyto" #header-market-authorization>
      <div data-test="market-authorization" class="product-crop-protection__market-authorization">
        {{ i18n._('AMM :') }}
        {{ (product.market_authorization && product.market_authorization.toLocaleString()) || 'NC' }}
      </div>
    </template>
    <template #header-regulatory-display>
      <div>
        <p-product-alert-regulatory id="product-regulatory" :product="product" data-test="regulatory" />
      </div>
    </template>
    <template v-if="isSeed" #header-details>
      <div data-test="header-details" class="product-detail__seed-main-information">
        <span v-if="precocities">
          {{ i18n._('Précocité :') }}
          <strong>{{ precocities }}</strong>
        </span>
        <span v-if="product.supplier && isAladin">
          {{ i18n._('Fournisseur :') }}
          <strong>{{ product.supplier }}</strong>
        </span>
        <span v-if="product.valuation">
          {{ i18n._('Destination :') }}
          <strong>{{ product.valuation?.join(', ') }}</strong>
        </span>
        <span v-if="isStockAvailable" data-test="stock-available" class="product-detail__stock-available">
          <span class="adn-icon adn-icon--status-check" aria-hidden="true" />
          {{ i18n._('En stock') }}
        </span>
      </div>
    </template>
    <template #default>
      <p-product-detail-block
        v-if="(product.nutritive_elements && product.nutritive_elements.length > 0) || product.shape"
        id="product-detail"
        data-test="composition"
      >
        <div v-if="product.nutritive_elements && product.nutritive_elements.length > 0" data-test="nutritive-elements">
          <h2 class="product-detail__block-title" data-test-title>{{ i18n._('COMPOSITION CHIMIQUE') }}</h2>
          <div class="product-nutritive-elements__table">
            <div class="product-nutritive-elements__header">
              <p>{{ i18n._('Élément chimique') }}</p>
            </div>
            <div
              v-for="element in product.nutritive_elements"
              :key="element.name"
              class="product-nutritive-elements__table-item"
            >
              <div class="product-nutritive-elements__element">
                <p nutritive-elements-name>{{ element.name }}</p>
                <p class="product-nutritive-elements__percent" nutritive-elements-value>
                  {{ element.value }} {{ element.unit }}
                </p>
              </div>
              <div
                v-if="element.composition && element.composition.length > 0"
                class="product-nutritive-elements__table-subitems"
                data-test="nutritive-elements-composition"
              >
                <div
                  v-for="composition in element.composition"
                  :key="composition.name"
                  class="product-nutritive-elements__element"
                >
                  <p data-test-name>{{ composition.name }}</p>
                  <p data-test-value class="product-nutritive-elements__percent">
                    {{ composition.value }} {{ composition.unit }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="product.shape" data-test="shape">
          <h2 class="product-detail__block-title" data-test-title>{{ i18n._('FORME') }}</h2>
          <div class="shape" data-test="shape-value">{{ product.shape }}</div>
        </div>
      </p-product-detail-block>
      <p-product-detail-block
        v-if="product.active_substances && product.active_substances.length > 0"
        id="product-detail"
        data-test-composition
        class="product-detail__composition"
      >
        <div class="row">
          <div data-test="active-substances" class="col-md-6">
            <h2 class="product-detail__block-title" data-test-title>{{ i18n._('Matières actives') }}</h2>
            <div>
              <div
                v-for="substance in product.active_substances"
                :key="substance.name"
                class="product-active-substances__item-wrapper truncate"
                @click="searchActiveSubstance(substance.name)"
              >
                <div class="product-active-substances__item" @click="searchActiveSubstance(substance.name)">
                  <span class="product-active-substances__quantity">{{ substance.value }} {{ substance.unit }}</span>
                  <span class="product-active-substances__separation">-</span>
                  <span class="product-active-substances__name">{{ substance.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="product.formulation && product.formulation.length > 0" data-test-formulation class="col-md-6">
            <h2 class="product-detail__block-title" data-test-title>{{ i18n._('Formulation') }}</h2>
            {{ product.formulation }}
          </div>
        </div>
      </p-product-detail-block>
      <p-product-detail-block-identity
        v-if="isSeed"
        id="product-detail"
        data-test="identity"
        :title="i18n._(`Fiche d'identité`)"
        :identities="seedIdentities"
      ></p-product-detail-block-identity>
      <p-product-detail-block-html
        id="product-detail"
        :html="product.description"
        :title="i18n._('Descriptif')"
        data-test="description"
        :format-html="true"
      />
      <p-product-detail-block
        v-if="cooperativeName && isPhyto"
        class="product-crop-protection__adivalor"
        data-test="adivalor-notice"
      >
        <p>
          <img
            src="@b2ag/themes/aladin/assets/logo_adivalor.svg"
            class="product-crop-protection__adivalor-icon"
            alt="Advilor logo"
          />
          {{ cooperativeName }} {{ i18n._('gère le traitement des EVPP et des PPNU en coopération avec ADIVALOR') }}
        </p>
      </p-product-detail-block>
      <p-product-detail-block-html
        :html="product.characteristics"
        :title="i18n._('Caractéristiques')"
        data-test="characteristics"
      />
      <p-product-detail-block
        class="product-biocide-use"
        v-if="product.biocide_use && product.biocide_use.length > 0"
        :title="i18n._('Usage biocide')"
      >
        <p v-for="biocide in product.biocide_use" :key="biocide.code">
          {{ biocide.label }}
        </p>
      </p-product-detail-block>
      <p-product-detail-block
        v-if="product.usages_by_crops && product.usages_by_crops.length > 0"
        data-test="usages"
        class="no-padding no-border"
      >
        <p-product-usages id="product-usages" :product="product" data-test-product-usages />
      </p-product-detail-block>
      <p-product-detail-block-crops
        v-if="hasCrops"
        data-test="crops"
        :vegetative-stage-by-crops="product.culturesWithVegetativeStage"
      ></p-product-detail-block-crops>
      <p-product-detail-block-identity
        v-if="isEngrais"
        data-test="identity"
        :title="i18n._(`Fiche d'identité`)"
        :identities="engIdentities"
      ></p-product-detail-block-identity>
      <p-product-detail-block-review
        v-if="product.cooperativeReview"
        id="product-review"
        data-test="review"
        :cooperative-review="product.cooperativeReview"
        :cooperative-name="cooperativeName"
      ></p-product-detail-block-review>
      <p-product-detail-block-toxicity
        v-if="product.toxicities"
        data-test="toxicity"
        :toxicities="product.toxicities"
      />
      <p-product-detail-block-security-recommendations
        data-test="security-recommendations"
        :toxicities="product.toxicities"
        :biocide-families="product.biocideFamily"
      />
      <p-product-detail-block-documentation
        id="product-documentation"
        :product="product"
        data-test="documentation"
        :suffix="isPhyto ? i18n._('(dont étiquette)') : ''"
      />
    </template>

    <template v-if="isPhyto" #legal-mention>
      <div data-test="legal-mention" class="product-crop-protection__phyto-legal-mention">
        <div class="product-crop-protection__phyto-legal-mention-main-message">
          {{
            i18n._(
              `Produits pour les professionnels : utilisez les produits phytopharmaceutiques avec précaution. Avant toute utilisation, lisez l'étiquette et les informations concernant le produit.`,
            )
          }}
        </div>
        <div>
          {{
            i18n._(`Avant toute utilisation assurez-vous que celle ci est indispensable. Privilégiez chaque fois que possible les
          méthodes alternatives et les produits présentant le risque le plus faible pour la santé humaine et animale et
          pour l'environnement, conformément aux principes de la protection intégrée, consultez`)
          }}
          <a href="http://www.agriculture.gouv.fr/ecophyto" target="_blank">
            {{ i18n._('http://www.agriculture.gouv.fr/ecophyto') }}
          </a>
          .
        </div>
      </div>
    </template>
  </p-product-layout>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { i18n } from '@b2ag/locale'
import type { Profile } from './typings'
import PProductUsages from '@b2ag/usages/src/components/PProductUsages/PProductUsages.vue'
import PProductDetailBlock from '../PProductDetailBlock/PProductDetailBlock.vue'
import PProductExplosivesPrecursorCard from '../PProductExplosivesPrecursorCard/PProductExplosivesPrecursorCard.vue'
import PProductDetailBlockHtml from '../PProductDetailBlockHtml/PProductDetailBlockHtml.vue'
import PProductDetailBlockDocumentation from '../PProductDetailBlockDocumentation/PProductDetailBlockDocumentation.vue'
import PProductDetailBlockReview from '../PProductDetailBlockReview/PProductDetailBlockReview.vue'
import PProductDetailBlockCrops from '../PProductDetailBlockCrops/PProductDetailBlockCrops.vue'
import PProductDetailBlockIdentity from '../PProductDetailBlockIdentity/PProductDetailBlockIdentity.vue'
import PProductDetailBlockToxicity from '../PProductDetailBlockToxicity/PProductDetailBlockToxicity.vue'
import PProductDetailBlockSecurityRecommendations from '../PProductDetailBlockSecurityRecommendations/PProductDetailBlockSecurityRecommendations.vue'
import PProductLayout from '../PProductLayout/PProductLayout.vue'
import PAlert from '@b2ag/design-system/src/presenters/PAlert/PAlert.vue'
import { DisplayType, type Product, type SingleProduct } from '../typings'
import type { Banner } from '@b2ag/contexts/marketing/src/domain/blocs/Banner'
import { featuresForCurrentBrand } from '@b2ag/app-brands/dist/features'
import { isAladin } from '@b2ag/app-brands'
import { useStock } from '@b2ag/catalog/src/useStock'
import { buildIdentities, dedupPrecocities } from './helpers'
import { CROP_PROTECTION_KIND, NUTRITION_AMENDMENTS_KIND, SEED_KIND } from '../../domain/products.constants'
import PProductAlertRegulatory from '../PProductAlertRegulatory/PProductAlertRegulatory.vue'

export default defineComponent({
  name: 'PProductB2AG',
  components: {
    PProductLayout,
    PProductDetailBlockHtml,
    PProductDetailBlockDocumentation,
    PProductDetailBlockReview,
    PProductDetailBlockCrops,
    PProductDetailBlockIdentity,
    PProductDetailBlockToxicity,
    PProductDetailBlockSecurityRecommendations,
    PProductDetailBlock,
    PProductExplosivesPrecursorCard,
    PProductAlertRegulatory,
    PAlert,
    PProductUsages,
  },
  props: {
    product: {
      type: Object as PropType<SingleProduct>,
      default: () => ({}),
    },
    supplierRelatedProducts: {
      type: Array as PropType<Product[]>,
      default: () => [],
    },
    similarProducts: {
      type: Array as PropType<Product[]>,
      default: () => [],
    },
    frequentlyBoughtTogetherProducts: {
      type: Array as PropType<Product[]>,
      default: () => [],
    },
    supplierRelatedProductCount: {
      type: Number,
      default: 0,
    },
    profile: {
      type: Object as PropType<Profile>,
      default: () => ({}),
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    isExplosivesPrecursorAllowed: {
      type: Boolean,
      default: false,
    },
    showTechnicalSalesContact: {
      type: Boolean,
      default: true,
    },
    cooperativeName: {
      type: String,
      default: '',
    },
    isCropProtectionAllowed: {
      type: Boolean,
      default: false,
    },
    banner: {
      type: Object as PropType<Banner>,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const hasCompositionDetails = computed(() =>
      props.product.nutritive_elements?.some(
        (element) => 'composition' in element && element.composition && element.composition.length > 0,
      ),
    )

    const nutritiveElementsWithCompositionDetails = computed(() =>
      props.product.nutritive_elements?.filter(
        (element) => 'composition' in element && element.composition && element.composition.length > 0,
      ),
    )

    const hasCrops = computed(
      () => props.product.culturesWithVegetativeStage && props.product.culturesWithVegetativeStage.length,
    )

    const engIdentities = computed(() => {
      return [
        { label: 'Type', value: props.product.typeEng },
        { label: 'Origine', value: props.product.origin },
        { label: 'Mélange', value: props.product.mixed },
        { label: "Mode d'apport", value: props.product.contributionMode },
        { label: 'Norme', value: props.product.standard },
        { label: 'Substance stimulante', value: props.product.stimulatingSubstance },
      ]
    })

    const nutritiveElementUnit = computed(() => {
      let nutritiveElementsUnit = '%'
      if (props.product.nutritiveElementsUnit === 'g/l') {
        nutritiveElementsUnit = 'g/L'
      }
      return nutritiveElementsUnit
    })

    const isPhyto = computed(
      () => props.product.displayType === DisplayType.PHYTO || props.product.kind === CROP_PROTECTION_KIND,
    )
    const isSeed = computed(() => props.product.displayType === DisplayType.SEMENCE || props.product.kind === SEED_KIND)
    const isEngrais = computed(
      () => props.product.displayType === DisplayType.ENGRAIS || props.product.kind === NUTRITION_AMENDMENTS_KIND,
    )

    const precocities = dedupPrecocities(props.product)
    const seedIdentities = computed(() => buildIdentities(props.product, precocities, isAladin))
    const isManaged = computed(() => props.product?.variants[0]?.stockManagementType === 'managed')

    const hasOfferAllowedWithoutStock = computed(() =>
      props.product?.variants[0]?.offers?.some((offer) => offer.allowedWithoutStock),
    )

    const isStockAvailable = computed(() => {
      if (isManaged.value) {
        const stock = useStock(props.product?.variants[0])
        return hasOfferAllowedWithoutStock.value || stock.hasStock.value
      }
      return !!props.product?.variants[0]?.offers?.length
    })

    const searchActiveSubstance = (activeSubstance: string): void => emit('searchActiveSubstance', activeSubstance)
    const login = () => emit('login')
    const goToProductSearch = (goToProductSearchArgs: unknown) => emit('goToProductSearch', goToProductSearchArgs)

    return {
      login,
      goToProductSearch,
      i18n,
      hasCompositionDetails,
      nutritiveElementsWithCompositionDetails,
      hasCrops,
      engIdentities,
      nutritiveElementUnit,
      featuresForCurrentBrand,
      searchActiveSubstance,
      isPhyto,
      isAladin,
      isEngrais,
      precocities,
      seedIdentities,
      isStockAvailable,
      isSeed,
    }
  },
})
</script>

<style scoped lang="scss">
.product-detail__seed-main-information {
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-s);
  margin-top: 10px;

  @media (min-width: $breakpoint-sm) {
    font-size: var(--font-size-m);
  }
}

.product-detail__stock-available {
  padding-top: var(--spacing-2);
  color: var(--color-success);
  font-weight: 700;
  font-size: 17px;
}

.product-nutritive-elements__table {
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-1);
  margin-bottom: var(--spacing-4);

  > div {
    border-bottom: 1px solid var(--color-border-light);
  }

  > div:last-child {
    border-bottom: none;
  }
}

.product-nutritive-elements__header {
  padding: var(--spacing-2) var(--spacing-3);
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-4);
  font-size: var(--font-size-s);
  font-weight: 700;
  line-height: var(--line-height-s);
}

.product-nutritive-elements__element {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-4);
}

.product-nutritive-elements__table-item {
  padding: var(--spacing-3);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  color: var(--color-black);
  font-size: var(--font-size-m);
  line-height: var(--line-height-m);
}

.product-nutritive-elements__table-subitems {
  color: var(--color-grey-dark);
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.product-nutritive-elements__percent {
  text-wrap: nowrap;
}

.product-detail__supplier {
  display: block;
  font-weight: normal;
  text-transform: uppercase;
  font-size: 1.28rem;
  height: 1.2em;

  @media (min-width: $breakpoint-md) {
    font-size: 1.6rem;
    margin: 5px 0;
  }
}

.shape {
  border-radius: 30px;
  display: table-cell;
}

.product-crop-protection__market-authorization {
  display: block;
  font-weight: normal;
  text-transform: uppercase;
  font-size: var(--font-size-s);
  height: 1.2em;

  @media (min-width: $breakpoint-sm) {
    font-size: var(--font-size-m);
    margin: 5px 0;
  }
}

.product-active-substances {
  @media (min-width: $breakpoint-md) {
    margin: 0 0 2em;
    overflow: hidden;
    flex: 50%;
    bottom: 0;
    padding: 0;
    border: 0;
  }

  &__item-wrapper {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 30px;

      @media (min-width: $breakpoint-md) {
        margin-bottom: 0;
      }
    }
  }

  &__item {
    border: 1px solid black;
    border-radius: 32px;
    font-weight: normal;
    font-size: 1.6rem;
    line-height: 22px;
    padding: 5px 10px;
    display: inline-block;
    cursor: pointer;
  }

  &__separation {
    padding: 0 5px;
  }
}

.product-crop-protection__phyto-legal-mention {
  color: var(--color-inactive-dark);
  margin: 15px;
  font-size: 0.9em;

  @media (min-width: $breakpoint-lg) {
    margin-bottom: 50px;
  }
}

.product-crop-protection__phyto-legal-mention-main-message {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-alert__explosives-precursor-card {
  margin-bottom: var(--spacing-3);
  margin-top: var(--spacing-2);
}

.product-crop-protection__adivalor {
  color: var(--color-text-light);
  margin-top: 0;
  border-top: none;
}

.product-crop-protection__adivalor-icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.product-biocide-use {
  margin-top: 0;
  border-top: none;
  p {
    font-size: var(--font-size-m);
    color: var(--color-text-light);
  }
}
</style>
